import { BadgeCheck, Building2, Globe, Mail, Phone } from 'lucide-react';
import {
  describeInterval,
  displayName,
  humanizeStatus,
  statusTone,
  type PlatformUser,
} from '@features/users/model/user.types';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { formatDate } from '@shared/lib/format-date';
import { formatPrice } from '@shared/lib/format-price';

interface UserDetailDialogProps {
  /** The user being inspected, or null when the dialog is closed. */
  user: PlatformUser | null;
  onClose: () => void;
}

/**
 * Everything the admin knows about one user, in the order it is asked for:
 * who they are, what they are paying for, what they have built, then the
 * account details that only matter once you need them.
 *
 * Read-only. Assigning demo access is a separate, deliberate action from the
 * row — opening a record should never be a step towards changing it.
 */
export function UserDetailDialog({ user, onClose }: UserDetailDialogProps) {
  const subscription = user?.subscription;
  const name = user ? displayName(user) : null;

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title={name ?? user?.email ?? 'User'}
      subtitle={
        user && (
          <span className="flex items-center gap-1.5">
            <Mail aria-hidden className="h-3.5 w-3.5 shrink-0" />
            {/* Only when the title is the name — otherwise this is the title. */}
            <span className="truncate">{name ? (user.email ?? 'No email') : 'No name on file'}</span>
            {user.emailVerified && (
              <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-periscope-700">
                <BadgeCheck aria-hidden className="h-3.5 w-3.5" />
                verified
              </span>
            )}
          </span>
        )
      }
      className="max-w-lg"
      footer={
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      {user && (
        <div className="space-y-5">
          <section>
            <SectionLabel>Subscription</SectionLabel>

            {subscription ? (
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="min-w-0 truncate text-sm font-medium text-slate-900">
                    {subscription.planName ?? 'Unnamed plan'}
                  </p>
                  <Badge tone={statusTone(subscription.status)}>
                    {humanizeStatus(subscription.status)}
                  </Badge>
                </div>

                <p className="mt-0.5 text-xs text-slate-500">
                  {formatPrice(subscription.price, subscription.currency) ?? 'No price'}
                  {describeInterval(subscription) && ` · ${describeInterval(subscription)}`}
                </p>

                <dl className="mt-3 space-y-1.5 border-t border-slate-100 pt-2.5">
                  <Row label="Started" value={formatDate(subscription.currentPeriodStart)} />
                  <Row label="Renews or ends" value={formatDate(subscription.currentPeriodEnd)} />
                  {subscription.trialEnd && (
                    <Row label="Trial ends" value={formatDate(subscription.trialEnd)} />
                  )}
                  {subscription.cancelledAt && (
                    <Row label="Cancelled" value={formatDate(subscription.cancelledAt)} />
                  )}
                  <Row
                    label="Billing"
                    value={subscription.stripeSubscriptionId ? 'Stripe' : 'Granted by an admin'}
                  />
                </dl>
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center text-sm text-slate-400">
                No subscription — this user has never been on a plan.
              </p>
            )}
          </section>

          <section>
            <SectionLabel>Activity</SectionLabel>

            <div className="grid grid-cols-3 gap-2">
              <Figure label="Budgets" value={String(user.budgetCount)} />
              <Figure label="Signed up" value={formatDate(user.createdAt)} />
              <Figure
                label="Last sign in"
                value={user.lastSignInAt ? formatDate(user.lastSignInAt) : 'Never'}
              />
            </div>
          </section>

          {(user.organization || user.phone || user.country) && (
            <section>
              <SectionLabel>Details</SectionLabel>

              <ul className="space-y-2 text-sm">
                {user.organization && (
                  <Detail icon={Building2} value={user.organization} label="Organization" />
                )}
                {user.phone && <Detail icon={Phone} value={user.phone} label="Phone" />}
                {user.country && <Detail icon={Globe} value={user.country} label="Country" />}
              </ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </p>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <dt className="text-slate-500">{label}</dt>
      <dd className="tabular-nums text-slate-900">{value}</dd>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-2 py-2.5 text-center">
      <p className="truncate text-sm font-semibold text-slate-900" title={value}>
        {value}
      </p>
      <p className="mt-0.5 text-[11px] leading-tight text-slate-500">{label}</p>
    </div>
  );
}

function Detail({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Phone;
  label: string;
  value: string;
}) {
  return (
    <li className="flex items-center gap-2.5">
      <Icon aria-hidden className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="w-24 shrink-0 text-xs text-slate-500">{label}</span>
      <span className="min-w-0 truncate text-slate-700">{value}</span>
    </li>
  );
}
